import {useCallback, useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import {attributionService} from '@/services/attributionService';
import type {AfriexQueryParams} from '@/types';
import type {AppUsageEvent} from '@/types/attribution';
import {AttributionEventType} from '@/types/attribution';
import {getErrorMessage} from '@/utils/errorMessage';

import useFilters from './useFilters';

export const useAppUsageHistory = () => {
  const allEventTypes = useMemo(() => Object.values(AttributionEventType), []);
  const [data, setData] = useState<AppUsageEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const isSearch = false;
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<
    AfriexQueryParams & {events: AttributionEventType[]}
  >({
    events: [],
  });
  const [filtersToApply, setFiltersToApply] = useState<
    AfriexQueryParams & {events: AttributionEventType[]}
  >({events: []});
  const [filterCount, setFilterCount] = useState(0);
  const {applyFilters} = useFilters(setCurrentFilters);

  const fetchList = useCallback(
    async (params?: AfriexQueryParams & {events: AttributionEventType[]}) => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await attributionService.getAppUsageHistory({
          ...params,
          events: params?.events?.length ? params.events : allEventTypes,
          limit,
        });
        setData(result.data);
        setPage(result.page);
        setTotalItems(result.total);
      } catch (err) {
        toast.error(getErrorMessage(err, 'An error occurred'));
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    // Fire-and-forget: fetchList catches and toasts its own failures, so this
    // only stops an unexpected throw becoming an unhandled rejection.
    fetchList(currentFilters).catch(() => {});
  }, [currentFilters, fetchList]);

  const handleApplyFilters = useCallback(() => {
    const applied = applyFilters(filtersToApply);
    // Pagination reset is success-only work. applyFilters returns false when it
    // rejects a date range, and Pagination reacts to this flag by calling
    // onPageChange -> a refetch, so toggling it unconditionally fired a request
    // the user never asked for. See hooks/useFilters.ts.
    if (!applied) {
      return false;
    }

    setIsPageReset((prev) => !prev);
    return true;
  }, [applyFilters, filtersToApply]);

  // Handle change for user manipulation
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  // Handle dates for applied filters
  const selectAppliedFilters = useCallback(
    (name: string, value: string | number | Date | string[] | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const handleAutoComplete = (value: string) => {
    setCurrentFilters((prev) => ({
      ...prev,
      userId: value?.trim(),
      events: allEventTypes,
    }));
  };

  const cleanFilters = () => {
    setCurrentFilters({events: allEventTypes});
    setFiltersToApply({events: allEventTypes});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  };

  return {
    data,
    isLoading,
    error,
    isSearch,
    isPageReset,
    pageInfo: {
      page,
      total: totalItems,
    },
    fetchList,
    filterCount,
    currentFilters,
    filtersToApply,
    clearFilters: cleanFilters,
    selectAppliedFilters,
    handleApplyFilters,
    selectCurrentFilters,
    handleAutoComplete,
  };
};
